import React from 'react';
import { X, Award, User, ThumbsUp, BookOpen, Phone, Mail, Building, DollarSign, Clock, Calendar, Globe, Languages } from 'lucide-react';
import { useState } from 'react';
import TherapistImage from './TherapistImage';
import { formatLabel, formatList } from './formatLabel';

// Modal component for detailed view
export default function TherapistView({ therapist, isOpen, onClose }) {
  if (!isOpen || !therapist) return null;

  const formatPhone = (phone) => {
    if (!phone) return 'Not provided';
    const phoneStr = phone.toString();
    if (phoneStr.length === 10) {
      return `(${phoneStr.slice(0, 3)}) ${phoneStr.slice(3, 6)}-${phoneStr.slice(6)}`;
    }
    return phoneStr;
  };

  const formatAddress = (address) => {
    if (!address) return 'Not provided';
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.postal_code) parts.push(address.postal_code);
    return parts.length > 0 ? parts.join(', ') : 'Not provided';
  };

  const formatInsuranceName = formatLabel;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-[90%] h-[90%] max-w-6xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 to-blue-600 text-white py-8 px-4 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="flex items-start gap-6">
            <TherapistImage
              firstName={therapist.firstName}
              lastName={therapist.lastName}
              size="large"
            />
            <div className="flex-grow">
              <h2 className="text-4xl font-bold mb-2">
                {therapist.firstName} {therapist.lastName}
              </h2>
              <div className="flex flex-wrap gap-4 text-blue-100">
                {therapist.licenseType && (
                  <div className="flex items-center gap-2">
                    <Award className="w-5 h-5" />
                    <span>{therapist.licenseType}</span>
                  </div>
                )}
                {therapist.pronouns && (
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    <span>{therapist.pronouns.replace(/_/g, '/')}</span>
                  </div>
                )}
                {therapist.endorsements && (
                  <div className="flex items-center gap-2">
                    <ThumbsUp className="w-5 h-5" />
                    <span>{therapist.endorsements} Endorsements</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-grow overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Biography */}
              {therapist.biography && (
                <div className="bg-gray-50 rounded-lg p-5">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                    About
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {therapist.biography}
                  </p>
                </div>
              )}

              {/* Identity & Demographics */}
              {therapist.identity && (
                <div className="bg-gray-50 rounded-lg p-5">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-600" />
                    Identity
                  </h3>
                  <p className="text-gray-700">
                    {therapist.identity}
                  </p>
                </div>
              )}

              {/* Languages */}
              {therapist.languages && (
                <div className="bg-gray-50 rounded-lg p-5">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Languages className="w-5 h-5 text-blue-600" />
                    Languages Spoken
                  </h3>
                  <p className="text-gray-700">• {formatList(therapist.languages)}</p>
                </div>
              )}
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Contact Information */}
              <div className="bg-blue-50 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Contact Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-blue-600" />
                    <span className="text-gray-700">{formatPhone(therapist.phone)}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <a href={`mailto:${therapist.email}`} className="text-blue-600 hover:underline">
                      {therapist.email}
                    </a>
                  </div>
                  <div className="flex items-start gap-3">
                    <Building className="w-5 h-5 text-blue-600 mt-0.5" />
                    <span className="text-gray-700">{formatAddress(therapist.officeAddress)}</span>
                  </div>
                </div>
              </div>

              {/* Session Details */}
              <div className="bg-gray-50 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Session Details</h3>
                <div className="space-y-3">
                  {/* {therapist.price && (
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-5 h-5 text-blue-600" />
                      <span className="text-gray-700">{therapist.price}</span>
                    </div>
                  )} */}
                  {therapist.sessionType && (
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-blue-600" />
                      <span className="text-gray-700">Session Type: {formatList(therapist.sessionType)}</span>
                    </div>
                  )}
                  {/* <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <span className="text-gray-700">
                      {therapist.acceptingNewPatients === 'Yes'
                        ? 'Accepting new patients for brief courses'
                        : 'Not currently accepting new patients'}
                    </span>
                  </div> */}
                </div>
              </div>

              {/* Insurance */}
              <div className="bg-gray-50 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Insurance Accepted</h3>
                <div className="flex flex-wrap gap-2">
                  {therapist.insurance && therapist.insurance.length > 0 ? (
                    therapist.insurance.map((ins, idx) => (
                      <span key={idx} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                        {formatInsuranceName(ins)}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500">No insurance information available</span>
                  )}
                </div>
              </div>

              {/* Licensed States */}
              <div className="bg-gray-50 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                  Licensed States
                </h3>
                <div className="flex flex-wrap gap-2">
                  {therapist.states && therapist.states.length > 0 ? (
                    therapist.states.map((state, idx) => (
                      <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                        {state}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500">No state information available</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 p-6">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Last updated: {new Date().toLocaleDateString()}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
             
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
